import {
  Button,
  Cascader,
  Drawer,
  Form,
  Input,
  Select,
  Spin,
  message,
} from 'antd';
import { getMemberById, getMemberList } from 'apis/ums';
import {
  productWantRequestCreate,
  productWantRequestUpdate,
} from 'apis/product-want-request';
import LOCALS from 'commons/locals';
import getCascaderFilter from 'utils/getCascaderFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { DefaultOptionType, SelectOption } from 'types/base';
import {
  PmsProductWantRequestFormValues,
  PmsProductWantRequestMember,
  PmsProductWantRequestMemberOption,
  PmsProductWantRequestRecord,
  PmsProductWantRequestStatus,
} from 'types/pms-product-want-request';
import { UmsMember } from 'types/ums';
import {
  getWantRequestStatusSelectOptions,
  getMaterialOptionsForCategory,
  recordToFormValues,
  toSaveDto,
} from './common';

type Props = {
  open: boolean;
  record?: PmsProductWantRequestRecord;
  onClose: () => void;
  onSuccess: () => void;
};

const formatMemberSelectLabel = (member: PmsProductWantRequestMemberOption) => {
  const name =
    member.showName ||
    [member.firstName, member.lastName].filter(Boolean).join(' ');
  return name ? `${name}(${member.email})` : member.email;
};

const toMemberOptionFromRecord = (
  member: NonNullable<PmsProductWantRequestMember>,
): PmsProductWantRequestMemberOption => ({
  id: member.id,
  email: member.email,
  phone: member.phone,
  firstName: member.firstName,
  lastName: member.lastName,
  showName:
    [member.firstName, member.lastName].filter(Boolean).join(' ') || undefined,
});

const toMemberOptionFromUms = (
  member: UmsMember,
): PmsProductWantRequestMemberOption => ({
  id: member.id,
  email: member.email,
  phone: member.phone,
  firstName: member.firstName,
  lastName: member.lastName,
  showName: member.showName,
});

const EditDrawer = ({ open, record, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<PmsProductWantRequestFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberList, setMemberList] = useState<
    PmsProductWantRequestMemberOption[]
  >([]);

  const {
    productCategoryCascaderOptions,
    rankSelectOptions,
    colorSelectOptions,
    hardwareSelectOptions,
    stampSelectOptions,
    materialCascaderOptionsMap,
  } = useAppSelector(selectGlobalInfo);

  const statusSelectOptions = useMemo(
    () => getWantRequestStatusSelectOptions(t),
    [t],
  );

  const productCategoryIdList = Form.useWatch('productCategoryIdList', form);
  const firstLevelId = productCategoryIdList?.[0];

  const materialCascaderOptions = useMemo(() => {
    if (!firstLevelId) return [];
    return materialCascaderOptionsMap[firstLevelId] ?? [];
  }, [firstLevelId, materialCascaderOptionsMap]);

  const rankOptions = useMemo(() => {
    if (!rankSelectOptions?.length) return [];
    return [...rankSelectOptions].sort((a, b) => (b.sort ?? 0) - (a.sort ?? 0));
  }, [rankSelectOptions]);

  const hardwareOptions = useMemo(() => {
    if (!hardwareSelectOptions?.length) return [];
    return [...hardwareSelectOptions].sort(
      (a, b) => (b.sort ?? 0) - (a.sort ?? 0),
    );
  }, [hardwareSelectOptions]);

  const stampOptions = useMemo(() => {
    if (!stampSelectOptions?.length) return [];
    return [...stampSelectOptions].sort(
      (a, b) => (b.sort ?? 0) - (a.sort ?? 0),
    );
  }, [stampSelectOptions]);

  useEffect(() => {
    if (!open) return;
    if (!record) {
      form.resetFields();
      form.setFieldsValue({
        status: PmsProductWantRequestStatus.PENDING_FOLLOWUP,
      });
      return;
    }
    if (!productCategoryCascaderOptions.length) return;

    const materialOpts = getMaterialOptionsForCategory(
      record.productCategoryId,
      productCategoryCascaderOptions,
      materialCascaderOptionsMap,
    );
    form.setFieldsValue(
      recordToFormValues(record, productCategoryCascaderOptions, materialOpts),
    );
  }, [
    open,
    record,
    form,
    productCategoryCascaderOptions,
    materialCascaderOptionsMap,
  ]);

  useEffect(() => {
    if (!open) {
      setMemberList([]);
      return;
    }
    if (!record?.memberId) {
      setMemberList([]);
      return;
    }

    if (record.member) {
      setMemberList([toMemberOptionFromRecord(record.member)]);
      return;
    }

    let cancelled = false;
    getMemberById(record.memberId).then(({ data }) => {
      if (!cancelled && data) {
        setMemberList([toMemberOptionFromUms(data)]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, record?.memberId, record?.member]);

  const handleSearchMember = useCallback((keyword: string) => {
    keyword = keyword.trim();
    if (!keyword) {
      setMemberList([]);
      return;
    }
    setMemberLoading(true);
    getMemberList({ keyword, pageNum: 1, pageSize: 10 })
      .then((res) => setMemberList(res.data.list.map(toMemberOptionFromUms)))
      .finally(() => setMemberLoading(false));
  }, []);

  const onMemberChange = (memberId: number) => {
    const member = memberList.find((m) => m.id === memberId);
    if (member) {
      form.setFieldsValue({
        email: member.email,
        phone: member.phone,
      });
    }
  };

  const cascaderFilter = useCallback(
    (inputValue: string, path: DefaultOptionType[]) =>
      getCascaderFilter(inputValue, path),
    [],
  );

  const optionFilter = useCallback(
    (input: string, option?: SelectOption) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const dto = toSaveDto(values);
      if (record?.id) {
        await productWantRequestUpdate(record.id, dto);
        message.success(t(LOCALS.pwr_update_success));
      } else {
        await productWantRequestCreate(dto);
        message.success(t(LOCALS.pwr_create_success));
      }
      onSuccess();
      onClose();
    } catch {
      // validation or api error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={
        record ? (
          <Trans i18nKey={LOCALS.pwr_edit_title} />
        ) : (
          <Trans i18nKey={LOCALS.pwr_create_title} />
        )
      }
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>
            <Trans i18nKey={LOCALS.cancel} />
          </Button>
          <Button type="primary" loading={submitting} onClick={onSubmit}>
            <Trans i18nKey={LOCALS.confirm} />
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<Trans i18nKey={LOCALS.pwr_member_optional} />}
          name="memberId"
        >
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder={t(LOCALS.phone_or_email) || ''}
            notFoundContent={memberLoading ? <Spin size="small" /> : null}
            onSearch={handleSearchMember}
            onChange={onMemberChange}
            options={memberList.map((m) => ({
              value: m.id,
              label: formatMemberSelectLabel(m),
            }))}
          />
        </Form.Item>
        <Form.Item
          label={<Trans i18nKey={LOCALS.email} />}
          name="email"
          dependencies={['phone']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value?: string) {
                const email = value?.trim();
                const phone = getFieldValue('phone')?.trim();
                if (!email && !phone) {
                  return Promise.reject(
                    new Error(
                      t(LOCALS.pwr_please_enter_email_or_phone) as string,
                    ),
                  );
                }
                if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                  return Promise.reject(
                    new Error(t(LOCALS.pwr_invalid_email) as string),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={<Trans i18nKey={LOCALS.pwr_phone} />}
          name="phone"
          dependencies={['email']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value?: string) {
                const phone = value?.trim();
                const email = getFieldValue('email')?.trim();
                if (email || phone) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t(LOCALS.pwr_please_enter_email_or_phone) as string),
                );
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={<Trans i18nKey={LOCALS.product_category} />}
          name="productCategoryIdList"
          rules={[
            {
              required: true,
              message: t(LOCALS.pwr_please_select_category) as string,
            },
          ]}
        >
          <Cascader
            className="w-full"
            options={productCategoryCascaderOptions}
            showSearch={{ filter: cascaderFilter }}
            placeholder={t(LOCALS.please_select) || ''}
            onChange={(value) => {
              const newFirst = value?.[0];
              const oldFirst = productCategoryIdList?.[0];
              if (String(newFirst ?? '') !== String(oldFirst ?? '')) {
                form.setFieldValue('materialPaths', []);
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label={<Trans i18nKey={LOCALS.material} />}
          name="materialPaths"
        >
          <Cascader
            className="w-full"
            multiple
            options={materialCascaderOptions}
            showSearch={{ filter: cascaderFilter }}
            placeholder={
              firstLevelId
                ? t(LOCALS.please_select) || ''
                : t(LOCALS.please_select_category_first) || ''
            }
          />
        </Form.Item>
        <Form.Item label={<Trans i18nKey={LOCALS.rank} />} name="ranks">
          <Select
            mode="multiple"
            allowClear
            options={rankOptions}
            placeholder={t(LOCALS.please_select) || ''}
          />
        </Form.Item>
        <Form.Item label={<Trans i18nKey={LOCALS.color} />} name="color">
          <Select
            mode="multiple"
            allowClear
            showSearch
            options={colorSelectOptions}
            filterOption={optionFilter}
            placeholder={t(LOCALS.please_select) || ''}
          />
        </Form.Item>
        <Form.Item label={<Trans i18nKey={LOCALS.hardware} />} name="hardware">
          <Select
            mode="multiple"
            allowClear
            showSearch
            options={hardwareOptions}
            filterOption={optionFilter}
            placeholder={t(LOCALS.please_select) || ''}
          />
        </Form.Item>
        <Form.Item label={<Trans i18nKey={LOCALS.stamp} />} name="stamp">
          <Select
            mode="multiple"
            allowClear
            showSearch
            options={stampOptions}
            filterOption={optionFilter}
            placeholder={t(LOCALS.please_select) || ''}
          />
        </Form.Item>

        <Form.Item
          label={<Trans i18nKey={LOCALS.pwr_follow_up_status} />}
          name="status"
        >
          <Select options={statusSelectOptions} />
        </Form.Item>
        <Form.Item label={<Trans i18nKey={LOCALS.remark} />} name="remark">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditDrawer;
